import { useMemo } from 'react';
import { useApiClient, useFetchInfiniteES } from '@marageti/z4-lib';
import {
  FilterConstraint,
  FilterOp,
  SearchRequest,
  SearchResult,
} from '@marageti/z4-sdk/lib/entity';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';

interface TicketsSearchParams {
  search?: string;
  type?: string[];
  assignee?: string;
  sort?: string;
  status?: string[];
  severity?: string[];
  partnerId?: string;
  travelerId?: string;
}

const buildFilterConstraints = ({
  search,
  type,
  assignee,
  status,
  severity,
  partnerId,
  travelerId,
}: TicketsSearchParams): { filters: FilterConstraint[]; fullText?: string } => {
  const filters: FilterConstraint[] = [];

  // Exclude REMINDER tickets
  filters.push({
    field: 'ticketType',
    op: FilterOp.FilterOpTerm,
    value: ['ISSUE'],
  });

  filters.push({
    field: 'status',
    op: FilterOp.FilterOpNot,
    value: ['CANCELED'],
  });

  if (type && type.length > 0) {
    filters.push({
      field: 'ticketSubType',
      op: FilterOp.FilterOpTerm,
      value: type,
    });
  }

  if (assignee) {
    filters.push({
      field: 'assignees.id',
      op: FilterOp.FilterOpTerm,
      value: [assignee],
    });
  }

  if (status && status.length > 0) {
    const statusValues = status.flatMap((s) => {
      if (s === 'CLOSED') return ['COMPLETED'];
      return [s];
    });
    filters.push({
      field: 'status',
      op: FilterOp.FilterOpTerm,
      value: statusValues,
    });
  }

  if (severity && severity.length > 0) {
    filters.push({
      field: 'outcomeSeverity',
      op: FilterOp.FilterOpTerm,
      value: severity,
    });
  }

  if (partnerId) {
    filters.push({
      field: 'partnerId',
      op: FilterOp.FilterOpTerm,
      value: [partnerId],
    });
  }

  if (travelerId) {
    filters.push({
      field: 'travelerId',
      op: FilterOp.FilterOpTerm,
      value: [travelerId],
    });
  }

  return { filters, fullText: search || undefined };
};

const parseSortParam = (sort: string): { field: string; order: string } => {
  const [field, order] = sort.split(':');
  return { field: field || 'createdAt', order: order || 'desc' };
};

const useTicketsSearch = ({
  search = '',
  type = [],
  assignee = '',
  sort = 'createdAt:desc',
  status = [],
  severity = [],
  partnerId,
  travelerId,
}: TicketsSearchParams = {}) => {
  const apiClient = useApiClient();

  const { filters, fullText } = buildFilterConstraints({
    search, type, assignee, status, severity, partnerId, travelerId,
  });
  const { field: sortField, order: sortOrder } = parseSortParam(sort);

  const searchRequest: SearchRequest = {
    filterBy: filters,
    fullText,
    pagination: { limit: 20, page: 1 },
    sortBy: [{ field: sortField, order: sortOrder }],
  };

  const {
    data,
    error,
    hasMore,
    isLoading,
    loadMore,
    mutate,
  } = useFetchInfiniteES<Ticket>(
    apiClient.ticketsClient.searchTickets,
    searchRequest,
    { revalidateOnFocus: true, revalidateFirstPage: true },
  );

  const tickets = useMemo(() => (
    data?.flatMap((searchResult: SearchResult<Ticket>) => searchResult.hits) || []
  ), [data]);

  const refetch = () => mutate();

  return {
    tickets,
    totalHits: data?.[0]?.totalHits || 0,
    isLoading,
    error,
    hasMore,
    loadMore,
    refetch,
  };
};

export default useTicketsSearch;
